import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideArrowLeft,
  lucideCircleCheck,
  lucideCopy,
  lucideCircleCheckBig,
  lucideCopyPlus,
  lucideEllipsis,
  lucideInfo,
  lucideKeyboard,
  lucidePanelLeft,
  lucidePanelRight,
  lucideScan,
  lucideEye,
  lucideMaximize,
  lucidePencil,
  lucideRedo2,
  lucideSettings,
  lucideUndo2,
  lucideTextCursorInput,
  lucideTriangleAlert,
  lucideZoomIn,
  lucideZoomOut,
} from '@ng-icons/lucide';
import { HlmBadgeImports } from '@spartan-ng/helm/badge';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmDropdownMenuImports } from '@spartan-ng/helm/dropdown-menu';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { HlmTooltipImports } from '@spartan-ng/helm/tooltip';
import { SaveButtonComponent } from '../../../../components/save-button/save-button.component';
import {
  VoucherType,
  otherVoucherType,
  voucherTypeLabel,
} from '../../../../services/voucher-design.service';
import { Issue } from '../../editor-geometry';

/** Name, status, zoom, preview, save and activate: the editor's top bar. */
@Component({
  selector: 'app-editor-toolbar',
  standalone: true,
  imports: [
    RouterModule,
    NgIcon,
    HlmBadgeImports,
    HlmButtonImports,
    HlmDropdownMenuImports,
    HlmInputImports,
    HlmSpinnerImports,
    HlmTooltipImports,
    SaveButtonComponent,
  ],
  providers: [
    provideIcons({
      lucideArrowLeft,
      lucideCircleCheck,
      lucideCopy,
      lucideCircleCheckBig,
      lucideCopyPlus,
      lucideEllipsis,
      lucideInfo,
      lucideKeyboard,
      lucidePanelLeft,
      lucidePanelRight,
      lucideScan,
      lucideEye,
      lucideMaximize,
      lucidePencil,
      lucideRedo2,
      lucideSettings,
      lucideUndo2,
      lucideTextCursorInput,
      lucideTriangleAlert,
      lucideZoomIn,
      lucideZoomOut,
    }),
  ],
  templateUrl: './editor-toolbar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditorToolbarComponent {
  readonly name = input.required<string>();
  readonly voucherType = input<VoucherType>('money');
  /** Copying a money design as a product design needs product vouchers on. */
  readonly busy = input(false);
  readonly dirty = input(false);
  readonly saving = input(false);
  readonly previewing = input(false);
  readonly canSave = input(true);
  readonly canActivate = input(false);
  readonly missingBranding = input(false);
  readonly isActive = input(false);
  /** What still blocks saving and what is worth a look: shown as a counter that
   * opens the list, so the page never jumps when an issue appears or goes. */
  readonly errors = input<Issue[]>([]);
  readonly warnings = input<Issue[]>([]);
  readonly errorCount = computed(() => this.errors().length);
  readonly warningCount = computed(() => this.warnings().length);
  readonly zoom = input(1);
  readonly longTexts = input(false);
  readonly canUndo = input(false);
  readonly canRedo = input(false);
  /** An undo or redo is waiting for the server. */
  readonly restoring = input(false);
  readonly showOutlines = input(false);
  readonly paletteOpen = input(true);
  readonly inspectorOpen = input(true);

  readonly save = output<void>();
  readonly preview = output<void>();
  readonly activate = output<void>();
  /** Duplicate; with a type, as a design of the other voucher type. */
  readonly copy = output<VoucherType | null>();
  readonly rename = output<string>();
  readonly zoomChange = output<number>();
  readonly fit = output<void>();
  readonly longTextsChange = output<boolean>();
  readonly undo = output<void>();
  readonly redo = output<void>();
  readonly outlinesChange = output<boolean>();
  readonly paletteToggle = output<void>();
  readonly inspectorToggle = output<void>();
  readonly shortcuts = output<void>();
  /** Select and scroll to the element an issue is about. */
  readonly focusElement = output<string>();
  /** Show the voucher at about its real size on screen. */
  readonly realSize = output<void>();

  private readonly saveButton = viewChild<SaveButtonComponent>('saveButton');

  readonly typeLabel = computed(() => voucherTypeLabel(this.voucherType()));
  readonly otherType = computed(() => otherVoucherType(this.voucherType()));
  readonly copyAsLabel = computed(() =>
    this.voucherType() === 'money' ? 'Als Produkt-Design kopieren' : 'Als Wert-Design kopieren',
  );
  readonly activateHint = computed(() => {
    if (this.isActive()) return `Dieses Design wird für ${this.typeLabel()}e verwendet`;
    if (!this.canSave()) return 'Zuerst alle Pflichtelemente platzieren';
    if (this.missingBranding()) return 'Logo und Banner zuerst unter „Marke & Branding“ hochladen';
    if (this.dirty()) return 'Zuerst speichern';
    return '';
  });

  readonly editingName = signal(false);
  private readonly nameField = viewChild<ElementRef<HTMLInputElement>>('nameField');

  startNameEdit() {
    this.editingName.set(true);
    setTimeout(() => {
      const field = this.nameField()?.nativeElement;
      field?.focus();
      field?.select();
    });
  }

  cancelNameEdit() {
    this.editingName.set(false);
  }

  onNameCommit(event: Event) {
    const value = (event.target as HTMLInputElement).value.trim();
    if (value && value !== this.name()) this.rename.emit(value);
    this.editingName.set(false);
  }

  showSaved(message: string) {
    this.saveButton()?.showSuccessMessage(message);
  }

  showSaveError(message: string) {
    this.saveButton()?.showErrorMessage(message);
  }
}
